import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

const columns = [
  { id: "index", label: "#", align: "left", width: 56, sortable: false },
  { id: "name", label: "Name", align: "left", sortable: true, bold: true },
  { id: "ownerDisplay", label: "Owner", align: "left", sortable: true },
  { id: "phoneNumber", label: "PhoneNumber", align: "left", sortable: true },
  { id: "bloodType", label: "BloodType", align: "left", sortable: true },
];

const TEXT_COLUMNS = columns.filter((column) => column.id !== "index" && column.id !== "bloodType");

export default function BloodDonorsTable({
  items,
  isLoading,
  emptyMessage,
  onRowClick,
  order,
  orderBy,
  onSort,
}) {
  const { t } = useTranslation();

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table data-testid="clinic.blood-donors.table" sx={{ minWidth: 700 }}>
          <MUI.TableHead>
            <MUI.TableRow
              sx={{
                "& th": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "grey.50"
                      : "rgba(255,255,255,0.04)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
              }}
            >
              {columns.map((column) => (
                <MUI.TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.id === "index" ? (
                    ""
                  ) : column.sortable ? (
                    <MUI.TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                      data-testid={`clinic.blood-donors.sort-${column.id}`}
                    >
                      {t(column.label)}
                    </MUI.TableSortLabel>
                  ) : (
                    t(column.label)
                  )}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={columns.length} sx={{ border: 0, py: 6 }}>
                  <MUI.Stack alignItems="center" justifyContent="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : items.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={columns.length} sx={{ border: 0, py: 3 }}>
                  <MUI.Typography variant="body1" color={"grayText"}>
                    {t(emptyMessage)}
                  </MUI.Typography>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              items.map((donor, index) => (
                <MUI.TableRow
                  hover
                  key={donor.id}
                  data-testid={`clinic.blood-donors.row.${donor.id}`}
                  onClick={() => onRowClick(donor)}
                  sx={{
                    cursor: "pointer",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <MUI.TableCell sx={{ color: "text.disabled" }}>
                    {index + 1}
                  </MUI.TableCell>
                  {TEXT_COLUMNS.map((column) => (
                    <MUI.TableCell
                      key={column.id}
                      data-testid={`clinic.blood-donors.row.${donor.id}.${column.id}`}
                    >
                      <MUI.Typography
                        variant="body2"
                        color={column.bold ? "text.primary" : "text.secondary"}
                        sx={column.bold ? { fontWeight: 600 } : undefined}
                      >
                        {donor[column.id] || "-"}
                      </MUI.Typography>
                    </MUI.TableCell>
                  ))}
                  <MUI.TableCell data-testid={`clinic.blood-donors.row.${donor.id}.bloodType`}>
                    {donor.bloodType ? (
                      <MUI.Chip
                        label={donor.bloodType}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <MUI.Typography variant="body2" color="text.disabled">
                        N/A
                      </MUI.Typography>
                    )}
                  </MUI.TableCell>
                </MUI.TableRow>
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
